import paragonBreakpoints from '@edx/paragon/dist/utils/breakpoints';

const CONTENTFUL_IMAGES_HOSTNAME = 'images.ctfassets.net';
const VALID_CONTENTFUL_PARAMS = ['fm', 'w', 'h', 'q'];

/**
 * Breakpoints object for Contentful Image.
 * @typedef {Object} BreakpointsType
 * @property {number} width Width of the image for that type of breakpoint.
 * @property {number} height Height of the image for that type of breakpoint.
 * @property {'extraExtraLarge' | 'extraLarge' | 'large' | 'medium' | 'small' | 'extraSmall'} type Type of breakpoint.
 */

/**
 * Available formats for Contentful Image API.
 * @typedef {'webp' | 'jpg' | 'png' | 'avif' | 'gif'} FormatType
 */

/**
 * Parameters object for transforming the Contentful Image.
 * @typedef {Object} ParamsType
 * @property {FormatType} fm Format.
 * @property {number | null} w Width.
 * @property {number} h Height.
 */

/**
 * Queried image url from Contentful.
 *
 * The hostname has to be `images.ctfassets.net`.
 * @typedef {string} ContentfulUrlType
 */

/**
 * Adds parameters to contentful image url.
 *
 * `fm` - format.
 * `w` - width, number of px.
 * `h` - height, number of px.
 *
 * @param {ContentfulUrlType} queriedUrl {@link ContentfulUrlType}
 * @param {ParamsType} params {@link ParamsType}
 * @returns {string} URL
 */
const addContentfulImageParams = (queriedUrl, params) => {
  const url = new URL(queriedUrl);

  // need to check if the URL is valid contentful URL, because
  // the parameters are specific to Contentful
  if (url && url.hostname === CONTENTFUL_IMAGES_HOSTNAME) {
    const { searchParams } = url;

    if (process.env.GATSBY_ENVIRONMENT !== 'development') {
      // if we're on a production build, serve the img through edx.org domain
      // and later use cloudflare workers to proxy to contentful
      url.hostname = 'www.edx.org';
      // add contentful to the beginning of the pathname so that cloudflare
      // worker can identify this request easier
      url.pathname = `/contentful${url.pathname}`;
    }

    // for SVG we don't want to add any additional parameters
    if (!queriedUrl.includes('.svg')) {
      if (!params?.w && !params?.h) {
        throw new Error(`Missing one of required size parameters for Contentful Image. URL: ${url}`);
      }
      Object.entries(params).forEach(([key, value]) => {
        if (key && VALID_CONTENTFUL_PARAMS.includes(key) && value) {
          searchParams.set(key, value);
        }
      });
    }

    return url.toString();
  }

  // if the URL isn't a valid contentful URL, just return it
  return queriedUrl;
};

/**
 * Returns a formatted URL with params for Contentful Images.
 *
 * Used in places where you don't use the Contentful Image component,
 * but still need to format the URL. If you need to add additional
 * parameters, then you should still use {@link addContentfulImageParams}.
 * @param {{ url: ContentfulUrlType, width: number, height: number }} queriedImage Image queried from Contentful.
 * @param {object} additionalParams Additional params to override the default parameters.
 * @returns {ContentfulUrlType} Formatted Contentful Image URL
 */
const getContentfulUrlFromQueriedImage = (queriedImage, additionalParams = {}) => {
  if (!queriedImage) {
    return null;
  }

  return addContentfulImageParams(queriedImage?.url, {
    w: queriedImage?.width, h: queriedImage?.height, fm: additionalParams.fm || 'webp', ...additionalParams,
  });
};

/**
 *
 * @param {[BreakpointsType]} breakpoints Array of {@link BreakpointsType}
 * @param {ContentfulUrlType} src {@link ContentfulUrlType}
 * @param {FormatType} format {@link FormatType}
 * @returns
 */
const generateUrlForBreakpoints = (breakpoints, src, format) => {
  if (breakpoints?.length > 0) {
    return breakpoints.reduce((acc, curr) => {
      const url = addContentfulImageParams(src, { fm: format, w: curr?.width, h: curr?.height });
      return [...acc, { ...curr, url }];
    }, []);
  }

  return [];
};

/**
 * Generates `srcSet` for the `<source>` of ContentfulImage component.
 *
 * @param {array} breakpointsWithUrl An array returned from {@link generateUrlForBreakpoints} function.
 * @returns {string} Formatted string for `srcSet`, eg: `"https://images.ctfassets.net/name.jpg?fm=webp&w=100&h=100 576w, https://images.ctfassets.net/name.jpg?fm=webp&w=150&h=150 768w"`.
 */
const generateSrcSet = (breakpointsWithUrl) => {
  if (breakpointsWithUrl?.length > 0) {
    return breakpointsWithUrl.map(({ url, type }) => `${url} ${paragonBreakpoints[type].minWidth}w`).join(', ');
  }

  return null;
};

export {
  addContentfulImageParams, getContentfulUrlFromQueriedImage, generateUrlForBreakpoints, generateSrcSet,
};
